// tabs/player-notes.ts — client-side port of the PC sheet's Player Notes tab
//   options/game/character_sheet_display/player_notes/_player_notes_inc.php
//
// Four free-text sections (Backstory / Organizations / Enemies / Other Notes), each
// rendered from caster.player_characters.<field> through the parse_text() port, with
// an owner-only inline Edit affordance that opens the existing sidelog edit form.
// Modernized layout: stacked cards with an accent rail + quiet empty states (the PHP
// was bold-label + <hr> + "None").

import type { CachedCaster } from '../types';
import { pcField } from '../read-helpers';
import { parseText } from '../../shared/parse-text';

interface NoteSection {
    key: string;
    title: string;
    /** sidelog edit-form include under options/tools/player_characters/forms/. */
    form: string;
}

const SECTIONS: ReadonlyArray<NoteSection> = [
    { key: 'backstory', title: 'Backstory', form: 'form_edit_player_backstory_inc.php' },
    { key: 'organizations', title: 'Organizations', form: 'form_edit_player_organizations_inc.php' },
    { key: 'enemies', title: 'Enemies', form: 'form_edit_player_enemies_inc.php' },
    { key: 'other_notes', title: 'Other Notes', form: 'form_edit_player_other_notes_inc.php' },
];

/** Owner-only Edit pencil — opens the sidelog form tab + AJAX-loads the edit form. */
function editAffordance(form: string, casterId: string): string {
    const onclick =
        `forceOpenSideLog();` +
        `var t=document.getElementById('pills_sidelog_form_tab'); if(t){t.click();}` +
        `queryData('${BASE_URL}options/tools/player_characters/forms/${form}','div_refresh_sidelog_form',{ caster_id: '${escapeHtml(casterId)}' });`;
    return `<button type="button" class="ft-pc-note__edit btn btn-sm btn-dark border" aria-label="Edit" onclick="${onclick}">${iconMap('edit')}</button>`;
}

function sectionCard(caster: CachedCaster, section: NoteSection, casterId: string, canEdit: boolean): string {
    const raw = pcField(caster, section.key);
    const text = raw === undefined || raw === null ? '' : String(raw);
    const body = text !== ''
        ? `<div class="ft-pc-note__text">${parseText(text)}</div>`
        : `<div class="ft-pc-note__empty">Nothing recorded yet.</div>`;
    const edit = canEdit ? editAffordance(section.form, casterId) : '';
    return `<section class="ft-pc-note">
        <header class="ft-pc-note__head">
            <h6 class="ft-pc-note__title">${escapeHtml(section.title)}</h6>
            ${edit}
        </header>
        <div class="ft-pc-note__body">${body}</div>
    </section>`;
}

export function buildPlayerNotesHtml(caster: CachedCaster, casterId: string): string {
    const canEdit = typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const cards = SECTIONS.map((s) => sectionCard(caster, s, casterId, canEdit)).join('');
    return `<div class="ft-pc-notes">${cards}</div>`;
}
